import { useEffect, useMemo, useState } from "react";
import {
  HiOutlinePencil,
  HiOutlineChevronDown,
  HiOutlineCheckCircle,
} from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { apiGet, apiPut } from "../../api/client";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useUserSession } from "../../context/UserContext";
import { useCache } from "../../context/CacheContext";
import { useWorkspaceData } from "@data";
import { Button, CustomerModal } from "@components";
import { Customer, Order } from "../../models";
import { ICustomer } from "../../types";

type Line = {
  cargoId: string;
  description?: string;
  quantity: number;
  unitPrice?: number | null;
  vat: number;
};
type Intake = {
  customerPending: boolean;
  pricingPending: boolean;
  reviewToken: string;
  quantity: number;
  customer: Partial<ICustomer>;
  customerId?: string | null;
  invoiceCustomer?: ICustomer;
  lines?: Line[];
  missing?: string[];
  needsAttention?: boolean;
  editable?: boolean;
  agreedPrice?: { amount: number; vat: number; includesVat: boolean };
};
const inputClass =
  "mt-1 w-full rounded-lg border border-border bg-surface text-text p-3 placeholder:text-secondary focus:border-lgb-primary focus:ring-1 focus:ring-lgb-primary";
export function DriverOrderReview({ order }: { order: Order }) {
  const { activeWorkspace } = useWorkspace();
  const { userSession } = useUserSession();
  const { customers } = useWorkspaceData();
  const { updateCacheKey } = useCache();
  const { t } = useTranslation("orders");
  const workspace = activeWorkspace?.workspaceId;
  const token = userSession?.token?.accessToken;
  const canReview = ["Owner", "Administrator"].includes(
    activeWorkspace?.role ?? "",
  );
  const [intake, setIntake] = useState<Intake>();
  const [customerId, setCustomerId] = useState(order.customerId ?? "");
  const [prices, setPrices] = useState<string[]>([]);
  const [vats, setVats] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [customerOpen, setCustomerOpen] = useState(false);
  const [activeCustomer, setActiveCustomer] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [errorKey, setErrorKey] = useState("driverOrder.reviewFailed");
  const [customerModal, setCustomerModal] = useState<"new" | "edit">();
  const [savedCustomer, setSavedCustomer] = useState<Customer>();
  const [revision, setRevision] = useState(0);
  const [editing, setEditing] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const path = `/api/drivers/order-intakes/${encodeURIComponent(order.id)}`;
  const lines: Line[] = intake?.lines ?? [
    { cargoId: "", quantity: intake?.quantity ?? 1, vat: 25 },
  ];
  useEffect(() => {
    let current = true;
    setIntake(undefined);
    setError(false);
    if (canReview && token && workspace)
      apiGet(token, path, workspace)
        .then((r) => {
          if (!current) return;
          const value = r.data as Intake;
          setIntake(value);
          if ("customerId" in value) setCustomerId(value.customerId ?? "");
          const items = value.lines ?? [{ unitPrice: null, vat: 25 }];
          setPrices(
            items.map((line) =>
              line.unitPrice == null ? "" : String(line.unitPrice),
            ),
          );
          setVats(items.map((line) => String(line.vat ?? 25)));
        })
        .catch(() => {
          if (current) setError(true);
        });
    return () => {
      current = false;
    };
  }, [canReview, token, workspace, path, revision]);
  const customerName = (c: Partial<ICustomer>) =>
    c.isCompany
      ? c.name || ""
      : [c.firstName, c.lastName].filter(Boolean).join(" ") || c.name || "";
  const available = useMemo(() => {
    const values = new Map<string, ICustomer>(customers.map((c) => [c.id, c]));
    if (intake?.invoiceCustomer)
      values.set(intake.invoiceCustomer.id, intake.invoiceCustomer);
    if (savedCustomer) values.set(savedCustomer.id, savedCustomer);
    return Array.from(values.values());
  }, [customers, savedCustomer, intake?.invoiceCustomer]);
  const selectedCustomer = available.find((c) => c.id === customerId);
  const filtered = available.filter((c) =>
    `${customerName(c)} ${c.organizationNumber || ""}`
      .toLowerCase()
      .includes(search.toLowerCase()),
  );
  const pending =
    intake?.needsAttention ??
    (intake?.customerPending || intake?.pricingPending);
  const editable =
    intake?.editable !== false &&
    !order.isInvoiced &&
    order.status !== "Cancelled";
  const customerDraft = useMemo(
    () => ({ ...Customer.default(), ...intake?.customer, id: "" }),
    [intake],
  );
  async function save(approve: boolean) {
    if (!token || !workspace || busy || !intake) return;
    setBusy(true);
    setError(false);
    setDraftSaved(false);
    try {
      const pricing = intake.lines
        ? {
            lines: lines.map((line, index) => ({
              cargoId: line.cargoId,
              unitPrice: prices[index]?.trim() ? Number(prices[index]) : null,
              vat: Number(vats[index] ?? 25),
            })),
          }
        : {
            unitPrice: prices[0]?.trim() ? Number(prices[0]) : null,
            vat: Number(vats[0] ?? 25),
          };
      await apiPut(
        token,
        path,
        {
          customerId: customerId || null,
          ...pricing,
          reviewToken: intake.reviewToken,
          ...(!approve ? { approve: false } : {}),
        },
        workspace,
      );
      if (approve) {
        setIntake({
          ...intake,
          customerPending: false,
          pricingPending: false,
          needsAttention: false,
          missing: [],
        });
        setEditing(false);
      } else {
        setDraftSaved(true);
        setRevision((v) => v + 1);
      }
      updateCacheKey();
    } catch (error) {
      const code = (error as { response?: { data?: { error?: string } } })
        .response?.data?.error;
      setErrorKey(
        code === "DRIVER_ORDER_CUSTOMER_INCOMPLETE"
          ? "driverOrder.customerIncomplete"
          : code === "DRIVER_ORDER_INVALID_PRICE"
            ? "driverOrder.priceInvalid"
            : "driverOrder.reviewFailed",
      );
      setError(true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section
      id="order-completion"
      aria-labelledby="order-completion-title"
      className="my-5 rounded-xl border border-border bg-surface p-5 sm:p-6 shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3
          id="order-completion-title"
          className="text-lg font-semibold text-text"
        >
          {t("driverOrder.completionTitle")}
        </h3>
        <span className="text-xs text-secondary">
          {t("driverOrder.reviewTitle")}
        </span>
      </div>
      {pending && (
        <p className="mt-2 inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-200">
          {t("driverOrder.missingData")}
        </p>
      )}
      {(!canReview || !editable || (intake && !pending && !editing)) && (
        <p className="mt-2 text-sm text-secondary">
          {t(
            !canReview
              ? "driverOrder.adminRequired"
              : !editable
                ? "driverOrder.closed"
                : "driverOrder.reviewed",
          )}
        </p>
      )}
      {(order.comment?.trim() || intake?.agreedPrice) && (
        <div className="mt-4 border-l-2 border-lgb-primary pl-3 text-sm text-text">
          <p className="font-medium">{t("driverOrder.driverMessage")}</p>
          {order.comment?.trim() && (
            <p className="mt-1 whitespace-pre-wrap break-words">
              {order.comment}
            </p>
          )}
          {intake?.agreedPrice && (
            <p className="mt-1">
              {t("driverOrder.driverAgreedPrice", {
                amount: intake.agreedPrice.amount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }),
                basis: t(
                  intake.agreedPrice.includesVat
                    ? "driverOrder.includingVat"
                    : "driverOrder.excludingVat",
                ),
                vat: intake.agreedPrice.vat,
              })}
            </p>
          )}
        </div>
      )}
      {draftSaved && (
        <p
          role="status"
          className="mt-4 flex items-start gap-2 text-sm text-text"
        >
          <HiOutlineCheckCircle
            aria-hidden="true"
            className="h-5 w-5 shrink-0 text-lgb-primary"
          />
          {t("driverOrder.draftSaved")}
        </p>
      )}
      {error && (
        <div role="alert" className="mt-3 text-sm text-red-600">
          <p>{t(errorKey)}</p>
          <button
            type="button"
            className="mt-2 underline"
            onClick={() => setRevision((v) => v + 1)}
          >
            {t("driverOrder.reload")}
          </button>
        </div>
      )}
      {canReview && intake && editable && !pending && !editing && (
        <Button
          variant="secondary"
          size="sm"
          type="button"
          onClick={() => {
            setEditing(true);
            setRevision((v) => v + 1);
          }}
        >
          {t("driverOrder.editReview")}
        </Button>
      )}
      {canReview && intake && editable && (pending || editing) && (
        <form
          className="mt-5"
          onChange={() => setDraftSaved(false)}
          onSubmit={(e) => {
            e.preventDefault();
            void save(true);
          }}
        >
          <fieldset disabled={busy} className="space-y-4">
            <div>
              <label
                htmlFor="invoice-customer"
                className="text-sm font-medium text-text"
              >
                {t("driverOrder.invoiceCustomer")}
              </label>
              <div className="relative mt-1">
                <input
                  id="invoice-customer"
                  role="combobox"
                  autoComplete="off"
                  aria-expanded={customerOpen}
                  aria-controls="invoice-customer-options"
                  aria-autocomplete="list"
                  aria-activedescendant={
                    customerOpen && filtered[activeCustomer]
                      ? `invoice-customer-${activeCustomer}`
                      : undefined
                  }
                  placeholder={t("driverOrder.chooseCustomer")}
                  value={
                    customerOpen
                      ? search
                      : selectedCustomer
                        ? customerName(selectedCustomer)
                        : ""
                  }
                  onFocus={() => {
                    setSearch("");
                    setCustomerOpen(true);
                    setActiveCustomer(0);
                  }}
                  onBlur={() => setCustomerOpen(false)}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCustomerOpen(true);
                    setActiveCustomer(0);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setCustomerOpen(false);
                    }
                    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                      e.preventDefault();
                      setCustomerOpen(true);
                      setActiveCustomer((i) =>
                        Math.max(
                          0,
                          Math.min(
                            filtered.length - 1,
                            i + (e.key === "ArrowDown" ? 1 : -1),
                          ),
                        ),
                      );
                    }
                    if (e.key === "Enter" && customerOpen) {
                      e.preventDefault();
                      if (filtered[activeCustomer]) {
                        setCustomerId(filtered[activeCustomer].id);
                        setCustomerOpen(false);
                        setDraftSaved(false);
                      }
                    }
                  }}
                  className={`${inputClass} !mt-0 pr-20`}
                />
                <HiOutlineChevronDown
                  aria-hidden="true"
                  className="pointer-events-none absolute right-3 top-4 h-4 w-4 text-secondary"
                />
                {selectedCustomer && (
                  <button
                    type="button"
                    title={t("driverOrder.editCustomer")}
                    aria-label={t("driverOrder.editCustomer")}
                    onClick={() => setCustomerModal("edit")}
                    className="absolute right-9 top-2 rounded p-2 text-secondary hover:text-lgb-primary focus:ring-2 focus:ring-lgb-primary"
                  >
                    <HiOutlinePencil aria-hidden="true" className="h-4 w-4" />
                  </button>
                )}
                {customerOpen && (
                  <ul
                    id="invoice-customer-options"
                    role="listbox"
                    aria-label={t("driverOrder.invoiceCustomer")}
                    className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-border bg-surface py-1 shadow-lg"
                  >
                    {filtered.map((c, index) => (
                      <li
                        id={`invoice-customer-${index}`}
                        key={c.id}
                        role="option"
                        aria-selected={c.id === customerId}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setCustomerId(c.id);
                          setCustomerOpen(false);
                          setDraftSaved(false);
                        }}
                        className={`cursor-pointer px-3 py-2 text-sm text-text hover:bg-surface-hover ${index === activeCustomer ? "bg-surface-hover" : ""}`}
                      >
                        {customerName(c)}
                        {c.organizationNumber ? (
                          <span className="ml-2 text-secondary">
                            {c.organizationNumber}
                          </span>
                        ) : null}
                      </li>
                    ))}
                    {!filtered.length && (
                      <li className="px-3 py-2 text-sm text-secondary">
                        {t("driverOrder.noCustomers")}
                      </li>
                    )}
                  </ul>
                )}
              </div>
              {!customerId && (
                <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
                  <p className="text-secondary">
                    {t("driverOrder.createCustomerRequired", {
                      name: customerName(intake.customer),
                    })}
                  </p>
                  <Button
                    variant="secondary"
                    size="sm"
                    type="button"
                    onClick={() => setCustomerModal("new")}
                  >
                    {t("driverOrder.registerCustomer")}
                  </Button>
                </div>
              )}
              {selectedCustomer &&
                intake.missing
                  ?.filter(
                    (reason) =>
                      !["customer", "pricing", "review"].includes(reason),
                  )
                  .map((reason) => (
                    <p
                      key={reason}
                      className="mt-2 text-sm text-amber-800 dark:text-amber-200"
                    >
                      {t(`driverOrder.missing.${reason}`)}
                    </p>
                  ))}
            </div>
            <div className="border-t border-border pt-4">
              <h4 className="text-sm font-medium text-text">
                {t("driverOrder.priceSection")}
              </h4>
            </div>
            {lines.map((line, index) => (
              <div key={line.cargoId || index} className="space-y-3 pb-2">
                {line.description && (
                  <p className="font-medium text-text">{line.description}</p>
                )}
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="text-sm text-text">
                    {t(
                      line.quantity === 1
                        ? "driverOrder.orderPrice"
                        : "driverOrder.unitPrice",
                    )}
                    <input
                      placeholder="0,00"
                      type="number"
                      required
                      min="0.01"
                      max="1000000"
                      step="any"
                      value={prices[index] ?? ""}
                      onChange={(e) =>
                        setPrices((values) =>
                          values.map((value, i) =>
                            i === index ? e.target.value : value,
                          ),
                        )
                      }
                      className={inputClass}
                    />
                  </label>
                  <label className="text-sm text-text">
                    {t("driverOrder.vat")}
                    <select
                      value={vats[index] ?? "25"}
                      onChange={(e) =>
                        setVats((values) =>
                          values.map((value, i) =>
                            i === index ? e.target.value : value,
                          ),
                        )
                      }
                      className={inputClass}
                    >
                      {[0, 12, 15, 25].map((v) => (
                        <option key={v} value={v}>
                          {v} %
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                <p className="text-sm text-secondary">
                  {t("driverOrder.total", {
                    quantity: line.quantity,
                    total: (Number(prices[index] || 0) * line.quantity).toFixed(
                      2,
                    ),
                  })}
                </p>
              </div>
            ))}
          </fieldset>
          <div className="sticky bottom-0 z-10 -mx-5 -mb-5 mt-5 flex flex-col gap-3 rounded-b-xl border-t border-border bg-surface p-4 sm:-mx-6 sm:-mb-6 sm:flex-row sm:items-center sm:justify-end sm:px-6">
            <div className="flex flex-wrap gap-2 sm:shrink-0">
              <Button
                variant="secondary"
                type="button"
                disabled={busy}
                onClick={() => void save(false)}
              >
                {t(busy ? "driverOrder.saving" : "driverOrder.saveDraft")}
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  busy ||
                  !customerId ||
                  !lines.length ||
                  prices.some((price) => !Number(price))
                }
              >
                {t("driverOrder.approve")}
              </Button>
            </div>
          </div>
        </form>
      )}
      {customerModal && (
        <CustomerModal
          data={
            customerModal === "new"
              ? customerDraft
              : (selectedCustomer ?? customerDraft)
          }
          isShowing
          isSubmitting={busy}
          onCancel={() => {
            if (!busy) setCustomerModal(undefined);
          }}
          onConfirm={async (data) => {
            if (!workspace || busy) return;
            setBusy(true);
            setError(false);
            try {
              const customer =
                customerModal === "edit" && selectedCustomer
                  ? await Customer.update(new Customer(selectedCustomer), data)
                  : await Customer.create(workspace, data);
              if (!customer) throw new Error("Customer save failed");
              setSavedCustomer(customer);
              setCustomerId(customer.id);
              setSearch("");
              setCustomerModal(undefined);
              updateCacheKey();
            } catch (_) {
              setError(true);
            } finally {
              setBusy(false);
            }
          }}
        />
      )}
    </section>
  );
}
